import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, Loader2, MapPin, Plus, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Read-only owner-side event list. Backed by GET /events/mine which
// returns every event across all campsites the owner owns or manages,
// already sorted by start_at DESC and enriched with campsite_name.
export default function OwnerEvents() {
  const { api } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/events/mine")
      .then(({ data }) => { if (!cancelled) setItems(Array.isArray(data) ? data : (data?.items || [])); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load events."); });
    return () => { cancelled = true; };
  }, [api]);

  return (
    <div className="flex flex-col gap-5" data-testid="owner-events-page">
      <div>
        <Link to="/owner" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: "var(--brand)" }} data-testid="owner-events-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Owner
        </Link>
        <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--on-surface)" }}>
              Events
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
              Every event across your campsites. Create new ones from the mobile app or right here.
            </p>
          </div>
          <Link to="/owner/events/new" className="btn btn-green shrink-0" data-testid="owner-events-new">
            <Plus className="w-4 h-4" /> New event
          </Link>
        </div>
      </div>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}
      {!items && !error && (
        <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading events…
        </div>
      )}
      {items && items.length === 0 && (
        <div className="card-elev p-8 text-center" data-testid="owner-events-empty">
          <Sparkles className="w-8 h-8 mx-auto opacity-40" style={{ color: "var(--on-surface-muted)" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>No events yet</div>
          <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>
            Events are a Campsite Owner Premium feature — create your first one from the mobile app.
          </div>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="owner-events-grid">
          {items.map((ev) => <EventCard key={ev.event_id} ev={ev} />)}
        </div>
      )}
    </div>
  );
}

function EventCard({ ev }) {
  const start = (ev.start_at || "").slice(0, 10);
  const end = (ev.end_at || "").slice(0, 10);
  const status = ev.status || (ev.is_active ? "active" : "draft");
  return (
    <Link to={`/owner/events/${ev.event_id}`} className="card-elev overflow-hidden hover:-translate-y-0.5 transition block"
          data-testid={`owner-event-${ev.event_id}`}>
      {ev.cover_url ? (
        <div className="aspect-[16/9] bg-black/10 overflow-hidden">
          <img src={ev.cover_url} alt="" className="w-full h-full object-cover" />
        </div>
      ) : null}
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-extrabold text-base leading-tight" style={{ color: "var(--on-surface)" }}>{ev.name}</h3>
          <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0"
                style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>{status}</span>
        </div>
        <div className="text-xs inline-flex items-center gap-1" style={{ color: "var(--on-surface-muted)" }}>
          <MapPin className="w-3.5 h-3.5" /> {ev.campsite_name || "—"}
        </div>
        <div className="text-xs inline-flex items-center gap-1" style={{ color: "var(--on-surface-muted)" }}>
          <CalendarDays className="w-3.5 h-3.5" /> {start}{end && end !== start ? ` → ${end}` : ""}
        </div>
        <div className="mt-1 grid grid-cols-3 gap-2 text-center">
          <Mini label="Tasks" value={ev.tasks_count ?? (ev.tasks?.length ?? 0)} />
          <Mini label="Joined" value={ev.joined_count ?? 0} />
          <Mini label="Reward" value={ev.reward_xp ? `+${ev.reward_xp}` : "—"} accent="premium" />
        </div>
      </div>
    </Link>
  );
}

function Mini({ label, value, accent = "brand" }) {
  return (
    <div className="rounded-xl py-2 px-2"
         style={{ background: "color-mix(in srgb, var(--brand) 6%, transparent)" }}>
      <div className="text-[9px] font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
        {label}
      </div>
      <div className="text-sm font-extrabold" style={{ color: `var(--${accent})` }}>{value}</div>
    </div>
  );
}
